import { useState, useRef, useEffect } from 'react';
import { XIcon, CropIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { ExportIcon } from '../../../custom/export-icon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../tooltip';
import { ImageEditor, type ImageEditorTexts } from './image-editor';
import { cn } from '../../../lib/utils';
import { DropZone } from './drop-zone';

export type { ImageEditorTexts };

/** Whether a URL points to an already-uploaded server resource (not a local blob or data URL) */
function isServerUrl(url: string | undefined): boolean {
	return !!url && (url.startsWith('http://') || url.startsWith('https://'));
}

export function ImageUpload(props: {
	id?: string;
	currentUrl: string | undefined;
	file: File | undefined;
	onChange: (file: File | undefined) => void;
	onRemove: () => void;
	label: string;
	readOnly: boolean;
	contain?: boolean;
	enableOpenInTab?: boolean;
	enableCrop?: boolean;
	aspectRatio?: { width: number; height: number };
	size?: 'default' | 'compact';
	children?: React.ReactNode;
	noImageText?: string;
	openInNewTabText?: string;
	cropImageText?: string;
	removeImageText?: string;
	dropZoneDescription?: string;
	imageEditorTexts?: Partial<ImageEditorTexts>;
}) {
	const size = props.size ?? 'default';

	// Compute preview URL synchronously to avoid flash when replacing images
	const prevFileRef = useRef<File | undefined>(undefined);
	const previewUrlRef = useRef<string | null>(null);
	if (props.file !== prevFileRef.current) {
		if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
		previewUrlRef.current = props.file ? URL.createObjectURL(props.file) : null;
		prevFileRef.current = props.file;
	}
	const preview = previewUrlRef.current;
	useEffect(
		() => () => {
			if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
		},
		[],
	);

	const [editorOpen, setEditorOpen] = useState(false);
	const [pendingFile, setPendingFile] = useState<File | null>(null);
	const [originalFile, setOriginalFile] = useState<File | null>(null);
	const [cropArea, setCropArea] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

	const displayUrl = preview ?? props.currentUrl;
	const isCompact = size === 'compact';

	const imgClass = props.contain
		? `${isCompact ? 'max-h-32' : 'max-h-48'} max-w-full min-h-24 min-w-24 object-contain mx-auto block`
		: 'size-full rounded-xl object-cover';

	const handleFiles = (files: File[]) => {
		const f = files[0];
		if (!f?.type.startsWith('image/')) return;
		if (props.enableCrop) {
			setOriginalFile(f);
			setCropArea(null);
		}
		props.onChange(f);
	};

	const handleRemove = () => {
		props.onChange(undefined);
		props.onRemove();
		if (props.enableCrop) {
			setOriginalFile(null);
			setCropArea(null);
		}
	};

	return (
		<div className="flex flex-col">
			{props.readOnly && !displayUrl && (
				<p className="text-muted-foreground text-sm">{props.noImageText ?? 'No image uploaded'}</p>
			)}
			<AnimatePresence initial={false}>
				{displayUrl && (
					<motion.div
						key="preview"
						initial={{ height: 0 }}
						animate={{ height: 'auto' }}
						exit={{ height: 0 }}
						transition={{ type: 'spring', visualDuration: 0.2, bounce: 0 }}
						className="overflow-hidden"
					>
						<div className="pb-3">
							<div
								className={cn('relative', props.contain ? 'mx-auto w-fit' : 'flex items-center justify-center')}
								style={
									props.aspectRatio && !props.contain
										? { aspectRatio: `${props.aspectRatio.width} / ${props.aspectRatio.height}` }
										: undefined
								}
							>
								<img src={displayUrl} alt="" className={imgClass} />
								{props.readOnly ? (
									isServerUrl(props.currentUrl) && (
										<TooltipProvider>
											<div className="absolute top-1 right-1">
												<Tooltip>
													<TooltipTrigger asChild>
														<a
															href={props.currentUrl}
															target="_blank"
															rel="noopener noreferrer"
															aria-label={props.openInNewTabText ?? 'Open in new tab'}
															className="inline-flex rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
														>
															<ExportIcon className="size-3.5" />
														</a>
													</TooltipTrigger>
													<TooltipContent>{props.openInNewTabText ?? 'Open in new tab'}</TooltipContent>
												</Tooltip>
											</div>
										</TooltipProvider>
									)
								) : (
									<TooltipProvider>
										<div className="absolute top-1 right-1 flex gap-1">
											{props.enableOpenInTab && isServerUrl(props.currentUrl) && (
												<Tooltip>
													<TooltipTrigger asChild>
														<a
															href={props.currentUrl}
															target="_blank"
															rel="noopener noreferrer"
															aria-label={props.openInNewTabText ?? 'Open in new tab'}
															className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
														>
															<ExportIcon className="size-3.5" />
														</a>
													</TooltipTrigger>
													<TooltipContent>{props.openInNewTabText ?? 'Open in new tab'}</TooltipContent>
												</Tooltip>
											)}
											{props.enableCrop && (props.file || originalFile) && (
												<Tooltip>
													<TooltipTrigger asChild>
														<button
															type="button"
															aria-label={props.cropImageText ?? 'Crop image'}
															onClick={() => {
																const source = originalFile ?? props.file;
																if (!source) return;
																setOriginalFile(source);
																setPendingFile(source);
																setEditorOpen(true);
															}}
															className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
														>
															<CropIcon className="size-3.5" />
														</button>
													</TooltipTrigger>
													<TooltipContent>{props.cropImageText ?? 'Crop image'}</TooltipContent>
												</Tooltip>
											)}
											<Tooltip>
												<TooltipTrigger asChild>
													<button
														type="button"
														aria-label={props.removeImageText ?? 'Remove image'}
														onClick={handleRemove}
														className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
													>
														<XIcon className="size-3.5" />
													</button>
												</TooltipTrigger>
												<TooltipContent>{props.removeImageText ?? 'Remove image'}</TooltipContent>
											</Tooltip>
										</div>
									</TooltipProvider>
								)}
							</div>
						</div>
						{props.children && <div className="pb-3">{props.children}</div>}
					</motion.div>
				)}
			</AnimatePresence>
			<div
				className="grid"
				style={{
					gridTemplateRows: props.readOnly ? '0fr' : '1fr',
					opacity: props.readOnly ? 0 : 1,
					transition: 'grid-template-rows 200ms, opacity 200ms',
				}}
			>
				<div className="-m-1 overflow-hidden p-1">
					<DropZone
						id={props.id}
						label={props.label}
						description={props.dropZoneDescription}
						className={isCompact ? 'py-8' : undefined}
						onFiles={handleFiles}
					/>
				</div>
			</div>
			{props.enableCrop && (
				<ImageEditor
					open={editorOpen}
					file={pendingFile}
					aspectRatio={props.aspectRatio}
					initialCropArea={cropArea || undefined}
					texts={props.imageEditorTexts}
					onSave={(croppedFile, newCropArea) => {
						if (!originalFile) setOriginalFile(pendingFile);
						setCropArea(newCropArea);
						props.onChange(croppedFile);
						setEditorOpen(false);
						setPendingFile(null);
					}}
					onOpenChange={(open) => {
						if (!open) {
							setEditorOpen(false);
							if (!props.file) {
								setPendingFile(null);
								setCropArea(null);
								setOriginalFile(null);
							}
						}
					}}
				/>
			)}
		</div>
	);
}
